import React from 'react';
import {useTranslation} from 'react-i18next';
import Dialog from 'components/Dialog';
import {DialogTypes} from 'common/names';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import AsyncContainer from 'components/AsyncContainer';
import Panel from 'components/Panel';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import Button from 'components/Button';
import DialogActions from '@mui/material/DialogActions';
import {usePostApi} from 'common/hooks/usePostApi';
import FormikTextField from 'components/inputs/FormikTextField';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import {AlertDialog} from 'components/dialogs/AlertDialog';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions, layoutSelectors} from 'features/layout/redux';
import selectors, {LICENSE_UNLIMITED_NUM_KEYS} from 'redux/license/selectors';
import Typography from '@mui/material/Typography';
import {IAddableSshKey} from 'types/AddableSshKey';
import {IAddableKey} from 'types/AddableKey';
import {IAddablePgpKey} from 'types/AddablePgpKey';
import {get} from 'lodash';
import {DiplomatObjectType, DiplomatObjectTypeUnion} from 'types/DiplomatObjectType';
import {Filter} from 'common/tsUtils';
import Formik from 'components/hook-form/Formik';
import FieldArray from 'components/hook-form/FieldArray';
import {OverwriteStatusCheckbox} from 'components/inputs/OverwriteStatusCheckbox';

export type RecoverKeysDilogProps = {
   data: {
      ssh?: boolean;
      objectType: Filter<
         DiplomatObjectTypeUnion,
         'SSH_PUBLIC_KEY' | 'SSH_KEY' | 'KEY_PAIR' | 'PUBLIC_KEY'
      >;
   };
   onClose: () => void;
};

const controls = {
   pgp: {
      deletedApi: api.deletedPgpKeys,
      recoverApi: api.recoverPgpKeys,
      idKey: 'keyID' as 'keyID',
   },
   ssh: {
      deletedApi: api.deletedSshKeys,
      recoverApi: api.recoverSshKeys,
      idKey: 'fingerprint' as 'fingerprint',
   },
};

const RecoverKeysDialog = ({onClose, data: {ssh, objectType}}: RecoverKeysDilogProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const license = useSelector(selectors.getLicense);
   const keysCount = useSelector(layoutSelectors.keysCountSelector);
   const [selected, setSelected] = React.useState<string[]>([]);
   const handleClick = (event: React.MouseEvent<unknown>, keyID: string) => {
      const selectedIndex = selected.indexOf(keyID);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
         newSelected = newSelected.concat(selected, keyID);
      } else if (selectedIndex === 0) {
         newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
         newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
         newSelected = newSelected.concat(
            selected.slice(0, selectedIndex),
            selected.slice(selectedIndex + 1)
         );
      }

      setSelected(newSelected);
   };
   const isSelected = (id: string) => selected.indexOf(id) !== -1;

   const control = ssh ? controls.ssh : controls.pgp;

   const {request, state, loading} = useGetApi<(IAddableSshKey | IAddablePgpKey)[]>(
      control.deletedApi
   );
   React.useEffect(() => request(), [request]);
   const {request: recover} = usePostApi(control.recoverApi);

   const handleRecover = React.useCallback(
      (values: {keys: (IAddableSshKey | IAddablePgpKey)[]}) => {
         const keys = values.keys.filter((item) =>
            selected.some((keyID) => keyID === get(item, control.idKey))
         );

         const onSuccess = () => {
            dispatch(layoutActions.loadTreeNav());
            onClose();
         };

         recover(JSON.stringify({keys}), {onSuccess});
      },
      [recover, selected, control.idKey, dispatch, onClose]
   );

   const isAnyNameDuplicated = React.useMemo(
      () => !!state?.find((k: IAddableKey) => k.duplicateKeyName),
      [state]
   );

   const filteredWithEmptyPass = React.useMemo(
      () =>
         state
            ?.filter((key) => key?.addedKey?.objectType === objectType)
            .map((key) => ({...key, passphrase: null})),
      [state, objectType]
   );

   const isSecret =
      objectType === DiplomatObjectType.enum.KEY_PAIR ||
      objectType === DiplomatObjectType.enum.SSH_KEY;

   if (filteredWithEmptyPass?.length === 0) {
      return (
         <AlertDialog
            title={t('Recover keys')}
            content={t('There are no deleted keys to recover.')}
            onClose={onClose}
         />
      );
   }
   const deletedKeysCount = state?.length || 0;
   const count = ssh ? keysCount.ssh : keysCount.openpgp;
   const allowedKeysCount = license.numKeys - count - selected.length;

   return (
      <Dialog
         maxWidth="lg"
         header={
            ssh
               ? DialogTypes.RecoverSSHPublicKeysDialog.dialogName
               : DialogTypes.RecoverOpenPgpPublicKeysDialog.dialogName
         }
         close={onClose}
      >
         <AsyncContainer loading={loading}>
            {filteredWithEmptyPass ? (
               <>
                  {license.numKeys !== LICENSE_UNLIMITED_NUM_KEYS &&
                  deletedKeysCount > allowedKeysCount ? (
                     <Typography>
                        {t(
                           'There are {{deletedKeysCount}} deleted keys. Your license only allows you to add {{allowedKeysCount}} at the current time',
                           {deletedKeysCount, allowedKeysCount}
                        )}
                     </Typography>
                  ) : null}
                  <Formik initialValues={{keys: filteredWithEmptyPass}} onSubmit={handleRecover}>
                     <>
                        <Panel>
                           <TableContainer>
                              <Table size={'small'}>
                                 <TableHead>
                                    <TableRow>
                                       <TableCell>{t('Recover')}</TableCell>
                                       {ssh ? (
                                          <TableCell>{t('Fingerprint')}</TableCell>
                                       ) : (
                                          <>
                                             <TableCell>{t('Key Type')}</TableCell>
                                             <TableCell>{t('Key ID')}</TableCell>
                                             <TableCell>{t('User ID')}</TableCell>
                                          </>
                                       )}
                                       <TableCell>{t('Diplomat Key Name')}</TableCell>
                                       {isSecret && (
                                          <TableCell>
                                             {isAnyNameDuplicated
                                                ? t('Recovered Key Passphrase')
                                                : t('Passphrase')}
                                          </TableCell>
                                       )}
                                       {isAnyNameDuplicated ? (
                                          <>
                                             <TableCell>{t('Overwrite')}</TableCell>
                                             {isSecret && (
                                                <TableCell>
                                                   {t('Existing Key Passphrase')}
                                                </TableCell>
                                             )}
                                          </>
                                       ) : null}
                                    </TableRow>
                                 </TableHead>
                                 <TableBody>
                                    <FieldArray
                                       name="keys"
                                       render={({fields}) =>
                                          fields.map((_key, rowIndex) => {
                                             const key = (_key as unknown) as
                                                | IAddableSshKey
                                                | IAddablePgpKey;
                                             const isItemSelected = isSelected(
                                                get(key, control.idKey)
                                             );
                                             const labelId = `checkbox-${get(key, control.idKey)}`;

                                             let errorTooltip;
                                             if (!ssh) {
                                                const keyType = (key as IAddablePgpKey).secret
                                                   ? t('Key Pair')
                                                   : t('Public Key');
                                                if ((key as IAddablePgpKey).duplicateKeyID) {
                                                   errorTooltip = t(
                                                      'Recovery disabled. {{keyType}} with Key ID {{keyID}} already exists.',
                                                      {
                                                         keyType,
                                                         keyID: (key as IAddablePgpKey).keyID,
                                                      }
                                                   );
                                                } else if (
                                                   (key as IAddablePgpKey).duplicateObjectName
                                                ) {
                                                   errorTooltip = t(
                                                      '{{keyType}} exists with name "{{name}}". Uncheck Import or change Diplomat Key Name.',
                                                      {
                                                         keyType,
                                                         name: (key as IAddablePgpKey).addedKey
                                                            .name,
                                                      }
                                                   );
                                                }
                                             }

                                             return (
                                                <TableRow
                                                   tabIndex={-1}
                                                   key={get(key, control.idKey)}
                                                   title={errorTooltip}
                                                >
                                                   <TableCell padding="checkbox">
                                                      <Checkbox
                                                         disabled={
                                                            (key as IAddablePgpKey)
                                                               .duplicateKeyID ||
                                                            (!isItemSelected &&
                                                               allowedKeysCount === 0)
                                                         }
                                                         checked={isItemSelected}
                                                         inputProps={{
                                                            'aria-labelledby': labelId,
                                                         }}
                                                         onClick={(event) =>
                                                            handleClick(
                                                               event,
                                                               get(key, control.idKey)
                                                            )
                                                         }
                                                      />
                                                   </TableCell>
                                                   {ssh ? (
                                                      <TableCell>
                                                         {(key as IAddableSshKey).fingerprint}
                                                      </TableCell>
                                                   ) : (
                                                      <>
                                                         <TableCell>
                                                            {
                                                               DiplomatObjectType[
                                                                  key.addedKey.objectType
                                                               ]
                                                            }
                                                         </TableCell>
                                                         <TableCell>
                                                            {(key as IAddablePgpKey).keyID}
                                                         </TableCell>
                                                         <TableCell>
                                                            {(key as IAddablePgpKey).userID}
                                                         </TableCell>
                                                      </>
                                                   )}
                                                   <TableCell>
                                                      <FormikTextField
                                                         name={`keys.${rowIndex}.name`}
                                                         disabled={!isItemSelected}
                                                      />
                                                   </TableCell>
                                                   {isSecret && (
                                                      <TableCell>
                                                         <FormikPassphrase
                                                            name={`keys.${rowIndex}.passphrase`}
                                                            disabled={!isItemSelected}
                                                         />
                                                      </TableCell>
                                                   )}
                                                   <TableCell>
                                                      {key.duplicateKeyName ? (
                                                         <OverwriteStatusCheckbox
                                                            status={key.overwriteStatus}
                                                            name={`keys.${rowIndex}.overwriteStatus`}
                                                            disabled={!isItemSelected}
                                                         />
                                                      ) : null}
                                                   </TableCell>
                                                   <TableCell>
                                                      {key.duplicateKeyName && isSecret ? (
                                                         <FormikPassphrase
                                                            name={`keys.${rowIndex}.existingPassphrase`}
                                                            disabled={!isItemSelected}
                                                         />
                                                      ) : null}
                                                   </TableCell>
                                                </TableRow>
                                             );
                                          })
                                       }
                                    />
                                 </TableBody>
                              </Table>
                           </TableContainer>
                        </Panel>
                        <DialogActions>
                           <Button type={'submit'} color="primary" disabled={selected.length === 0}>
                              {t('Ok')}
                           </Button>
                           <Button onClick={onClose}>{t('Cancel')}</Button>
                        </DialogActions>
                     </>
                  </Formik>
               </>
            ) : null}
         </AsyncContainer>
      </Dialog>
   );
};

export default RecoverKeysDialog;
